/* eslint-disable @typescript-eslint/no-unused-vars */
import { ChangeEvent, useEffect, useState } from 'react';
import { Alert, Avatar, Box, Button, FormGroupClassKey, Grid, LinearProgress, Paper, Snackbar, SnackbarCloseReason, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { VTextField, VForm, useVForm, IVFormErrors, VTextFieldData, VFieldMoney, AutoCompleteContaBancaria2 } from '../../../shared/forms';
import { FerramentasDeDetalhe, TelaBloqueioSalvando } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { LancamentoEntreConstasService } from '../../../shared/services/api/Financeiro/LancamentoEntreConstas';
import { useBloqueioSalvamento } from '../../../shared/hooks';

interface IFormData {
  identrada: string;
  idsaida: string;
  datalan: Date;
  numero: string;
  valor: number;
  historico: string;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  identrada: yup.string().required(),
  datalan: yup.date().required(),
  numero: yup.string().optional().default(''),
  valor: yup.number().required().moreThan(0.1),
  historico: yup.string().optional().default(''),
  idsaida: yup
    .string()
    .uuid('Conta de Débito inválida')
    .required('A Conta de Débito é obrigatória')
    .test(
      'idsaida-diferente',
      'A Conta de Débito não pode ser a mesma da Conta de Crédito',
      function (value) {
        const { identrada } = this.parent;
        if (!value || !identrada) return true;
        return value !== identrada;
      }
    ),
});

export const DetalheDeEntreContas: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const { formRef, save, saveAndClose, isSaveAndClose } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();
  const [status, setStatus] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const [openConfirm, setOpenConfirm] = useState(false);
  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if (tms === 'success') {
      navigate('/entrecontas');
    }
  };

  const handleDelete = () => {
    setOpenConfirm(true);
  };

  const handleConfirmDelete = () => {
    LancamentoEntreConstasService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOpenConfirm(false);
          setTms('success');
          setMsg('Registro apagado com sucesso!');
          setOpenmsg(true);
        }
      });
  };
  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };

  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      LancamentoEntreConstasService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            formRef.current?.setData(result);
            setStatus(true);
          }
        });
    } else {
      formRef.current?.setData({
        datalan: new Date(),
        numero: '',
        valor: 0,
        historico: '',
      });
      setStatus(false);
    }
  }, [id]);

  const handleSave = (dados: IFormData) => {
    if (!iniciarSalvamento()) return;

    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'novo') {
          LancamentoEntreConstasService
            .create(dadosValidados)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                cancelarSalvamento();
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
                concluirSalvamento();
              }
            });
        } else {
          setIsLoading(false);
          cancelarSalvamento();
        }
      })
      .catch((errors: yup.ValidationError) => {
        cancelarSalvamento();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;

          validationErrors[error.path] = error.message;
        });

        formRef.current?.setErrors(validationErrors);
      });
  };

  return (
    <>
      <TelaBloqueioSalvando
        open={isSaving}
        message="Salvando..."
        progress={saveProgress}
      />
      <LayoutBaseDePagina
      titulo={'Lançamento Entre Contas'}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoSalvarCarregando={isLoading}
          mostrarBotaoSalvarEFecharCarregando={isLoading}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo'}
          mostrarBotaoApagarCarregando={isLoading}
          mostrarBotaoImpressao={false}
          aoClicarEmSalvar={save}
          disableBotaoSalvar={status ? true : (isLoading || isSaving)}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate('/entrecontas')}
          mostrarBotaoVoltarCarregando={isLoading}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/entrecontas/novo')}
          mostrarBotaoNovoCarregando={isLoading}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave}placeholder={undefined} onPointerEnterCapture={undefined}onPointerLeaveCapture={undefined}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Snackbar open={openConfirm} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            severity="warning"
            variant="filled"
            sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleConfirmDelete}>
                  Sim
                </Button>
                <Button color="inherit" size="small" onClick={handleCancelDelete}>
                  Nâo
                </Button>
              </>
            }
          >
            Tem certeza que deseja excluir este registro?
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">

          <Grid container direction="column" padding={2} spacing={2}>

            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}
            <Grid item>
              <Typography variant='h6'>Dados</Typography>
            </Grid>

            <Grid container item direction="row" spacing={1} >
              <Grid container item direction="row" spacing={1} xs={12} sm={12} md={12} lg={12} xl={12}>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
                  <AutoCompleteContaBancaria2
                    name='identrada'
                    label='Conta de Credito'
                    tipo='banco'
                    isExternalLoading={status ? true : isLoading}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
                  <AutoCompleteContaBancaria2
                    name='idsaida'
                    label='Conta de Debito'
                    tipo='banco'
                    isExternalLoading={status ? true : isLoading}
                  />
                </Grid>
                <Grid item xs={6} sm={12} md={6} lg={3} xl={2}>
                  <VTextFieldData
                    fullWidth
                    name='datalan'
                    label='Data Emissão'
                    disabled={status ? true : isLoading}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={3} lg={3} xl={2}>
                  <VTextField
                    fullWidth
                    size='small'
                    name='numero'
                    disabled={status ? true : isLoading}
                    label='Nº Documento'
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={6} xl={2}>
                  <VFieldMoney
                    fullWidth
                    size='small'
                    name='valor'
                    label='Valor a transferir'
                    disabled={status ? true : isLoading}
                  />
                </Grid>

                <Grid item marginTop={2} xs={12} sm={12} md={12} lg={12} xl={12}>
                  <VTextField
                    fullWidth
                    name='historico'
                    disabled={status ? true : isLoading}
                    multiline
                    rows={2}
                    label='Histórico'
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </VForm>

      </LayoutBaseDePagina>
    </>
  );
};